import { useState } from "react";
import Swal from "sweetalert2";
import Button from "../Button";
import { Success, Error } from "../Modal";
import { deleteUserAccount, restoreUserAccount } from "../../Services/User";
import { adminSwalOptions } from "../../Utils/adminSwal";
import { format_date } from "../../Utils/helpers";
import { isAccountDeleted, isDeletionPending } from "../AccountDeletionBadge";

/**
 * Detail-page action: schedule a deletion (same checks as the apps) or cancel one during the grace period.
 */
export default function AccountDeletionActions({ user, label = "account", onChanged }) {
  const [loading, setLoading] = useState(false);

  if (!user?._id || user.role === "admin" || isAccountDeleted(user)) return null;

  const run = async (request, title) => {
    setLoading(true);
    try {
      const res = await request();
      onChanged?.(res);
      Success(title, res?.data?.message);
    } catch (err) {
      Error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      ...adminSwalOptions,
      title: `Delete this ${label}?`,
      html: `The account is closed now and permanently anonymised after the grace period (30 days).<br/>
             The user cannot reopen it by logging in; they are asked to contact support, and only an admin can cancel the deletion from here.<br/>
             Past deliveries, payments and payouts are kept.`,
      input: "textarea",
      inputLabel: "Reason (internal)",
      inputPlaceholder: "e.g. Requested by the user via support",
      showCancelButton: true,
      confirmButtonText: "Delete Account",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    run(() => deleteUserAccount(user._id, { reason: (result.value || "").trim() }), "Deletion scheduled");
  };

  const handleRestore = async () => {
    const result = await Swal.fire({
      ...adminSwalOptions,
      title: "Cancel account deletion?",
      text: `This account is set to be deleted on ${format_date(user.deletionScheduledFor, "MM/DD/YYYY")}. Cancelling keeps it; the user can log in again.`,
      showCancelButton: true,
      confirmButtonText: "Cancel Deletion",
      cancelButtonText: "Keep Scheduled",
    });
    if (!result.isConfirmed) return;
    run(() => restoreUserAccount(user._id), "Deletion cancelled");
  };

  return isDeletionPending(user) ? (
    <Button type="button" className="open-support-chat-btn" loading={loading} onClick={handleRestore}>
      <i className="fas fa-undo" aria-hidden="true" />
      Cancel Deletion
    </Button>
  ) : (
    <Button type="button" className="request-resubmit-btn" loading={loading} onClick={handleDelete}>
      Delete Account
    </Button>
  );
}
